import { HttpErrorResponse } from '@angular/common/http';
import { AfterViewInit, Component, ViewChild } from '@angular/core';
import { ActivatedRoute } from '@angular/router';
import { IOcbCPgpKey, IOcbUPgpKey, IOcbPgpKey, PgpKeysService, UserService, IOcbApiStringParameter, IOcbApiPrivateKeyParameter } from '@app/core';
import { TranslateService } from '@ngx-translate/core';
import { DxDataGridComponent } from 'devextreme-angular/ui/data-grid';
import { DxValidationGroupComponent } from 'devextreme-angular/ui/validation-group';
import { confirm } from 'devextreme/ui/dialog';
import notify from 'devextreme/ui/notify';
import { forkJoin, from as fromPromise, Observable } from 'rxjs';
import { filter, finalize, mergeMap, tap } from 'rxjs/operators';

@Component({
    selector: 'app-pgp-keys',
    templateUrl: './pgp-keys.component.html',
    styleUrls: ['./pgp-keys.component.scss'],
})
export class PgpKeysComponent implements AfterViewInit {

    @ViewChild('pgpKeys')
    public pgpGrid: DxDataGridComponent;

    @ViewChild('pgpKeyValidation')
    public pgpKeyValidationGroup: DxValidationGroupComponent;

    @ViewChild('pgpKeyRenameValidation')
    public pgpKeyRenameValidationGroup: DxValidationGroupComponent;

    @ViewChild('pgpKeyChangePrivateKeyValidation')
    public pgpKeyChangePrivateKeyValidationGroup: DxValidationGroupComponent;

    @ViewChild('pgpKeyChangePublicKeyValidation')
    public pgpKeyChangePublicKeyValidationGroup: DxValidationGroupComponent;

    // Main Data-Grid
    public dataSource: IOcbPgpKey[] = null;

    // Popup Display
    public isDisplayPopupVisible: boolean = false;
    public displayedPgpKey: IOcbPgpKey = null;

    // Popup New & Rename
    public isNewPopupVisible: boolean = false;
    public isRenamePopupVisible: boolean = false;
    public isChangePrivateKeyPopupVisible: boolean = false;
    public isChangePublicKeyPopupVisible: boolean = false;
    public selectedPgpKey: IOcbPgpKey = null;
    public isNew: boolean = null;


    /**
     * Boolean to show or hide the loading panel
     */
    public isLoadPanelVisible: boolean = false;

    /**
     * Internal identifier of the current environment
     */
    private currentEnvironmentId: string = '';

    constructor(
        private route: ActivatedRoute,
        private userService: UserService,
        private pgpKeysService: PgpKeysService,
        private translateService: TranslateService) {
    }

    public ngAfterViewInit(): void {
        this.route.params.subscribe(_ => {
            this.currentEnvironmentId = this.route.snapshot.params.id;
            this.loadDataSource();
        });
    }

    /**
     * Set Recipients context menu items
     * @param e Handle event
     */
    public onContextMenuPreparing(e) {
        if (e.row && e.row.rowType === 'data') {
            forkJoin(
                // Get translation messages
                this.translateService.get('_commons.rename'),
                this.translateService.get('_commons.changePublicKey'),
                this.translateService.get('_commons.changePrivateKey'),
                this.translateService.get('_commons.delete'),
            ).subscribe(
                messages => {
                    e.items = [];
                    e.items.push({ text: messages[0], icon: 'edit', onItemClick: (ev) => this.displayRenamePopup(ev, e.row.data) });
                    e.items.push({ text: messages[1], icon: 'key', onItemClick: (ev) => this.displayChangePublicKeyPopup(ev, e.row.data) });
                    e.items.push({ text: messages[2], icon: 'key', onItemClick: (ev) => this.displayChangePrivateKeyPopup(ev, e.row.data) });
                    e.items.push({ text: messages[3], icon: 'trash', onItemClick: (ev) => this.deletePgpKey(ev, e.row.data) });
                },
                err => this.handleError(err),
            );
        }
    }

    /**
     * Dipslay new popup
     */
    public displayNewPopup(): void {
        this.selectedPgpKey = this.getNewIOcbPgpKey();
        this.isNew = true;
        this.isNewPopupVisible = true;
    }

    /**
     * Display update popup
     * @param e Handle event
     * @param pgpKey PGP key data to update
     */
    public displayRenamePopup(e, pgpKey: IOcbPgpKey): void {
        e.event.stopPropagation();
        this.selectedPgpKey = this.getIOcbPgpKeyFromData(pgpKey);
        this.isNew = false;
        this.isRenamePopupVisible = true;
    }

    /**
     * Display update popup
     * @param e Handle event
     * @param pgpKey PGP key data to update
     */
    public displayChangePrivateKeyPopup(e, pgpKey: IOcbPgpKey): void {
        e.event.stopPropagation();
        this.selectedPgpKey = this.getIOcbPgpKeyFromData(pgpKey);
        this.isNew = false;
        this.isChangePrivateKeyPopupVisible = true;
    }

    /**
     * Display update popup
     * @param e Handle event
     * @param pgpKey PGP key data to update
     */
    public displayChangePublicKeyPopup(e, pgpKey: IOcbPgpKey): void {
        e.event.stopPropagation();
        this.selectedPgpKey = this.getIOcbPgpKeyFromData(pgpKey);
        this.isNew = false;
        this.isChangePublicKeyPopupVisible = true;
    }

    /**
     * Create a PGP key
     */
    public createPgpKey() {
        if (this.pgpKeyValidationGroup.instance.validate().isValid) {
            this.isLoadPanelVisible = true;
            const newPgpKey = this.getIOcbCPgpKeyFromData(this.selectedPgpKey);
            this.pgpKeysService.addPgpKey(newPgpKey)
                .pipe(
                    mergeMap(_ => this.translateService.get('pgpKeys.msgPgpKeyCreationComplete')),
                    finalize(() => this.isLoadPanelVisible = false))
                .subscribe(message => {
                    this.isNewPopupVisible = false;
                    this.loadDataSource();
                    notify(message, 'success', 3000);
                }, err => this.handleError(err));
        }
    }

    /**
     * Update a PGP key
     */
    public updatePgpKey() {
        if (this.pgpKeyRenameValidationGroup.instance.validate().isValid) {
            this.isLoadPanelVisible = true;
            const updatePgpKey = this.getIOcbUPgpKeyFromData(this.selectedPgpKey);
            this.pgpKeysService.updatePgpKey(this.selectedPgpKey.id, updatePgpKey)
                .pipe(
                    mergeMap(_ => this.translateService.get('pgpKeys.msgPgpKeyUpdateComplete')),
                    finalize(() => this.isLoadPanelVisible = false))
                .subscribe(message => {
                    this.isRenamePopupVisible = false;
                    this.loadDataSource();
                    notify(message, 'success', 3000);
                }, err => this.handleError(err));
        }
    }

    /**
     * Change the private key
     */
    public updatePrivateKey() {
        if (this.pgpKeyChangePrivateKeyValidationGroup.instance.validate().isValid) {
            this.isLoadPanelVisible = true;
            const privateKey = this.getPrivateKeyFromData(this.selectedPgpKey);
            this.pgpKeysService.changePrivateKey(this.selectedPgpKey.id, privateKey)
                .pipe(
                    mergeMap(_ => this.translateService.get('pgpKeys.msgPrivateKeyUpdateComplete')),
                    finalize(() => this.isLoadPanelVisible = false))
                .subscribe(message => {
                    this.isChangePrivateKeyPopupVisible = false;
                    this.loadDataSource();
                    notify(message, 'success', 3000);
                }, err => this.handleError(err));
        }
    }

    /**
     * Change the public key
     */
    public updatePublicKey() {
        if (this.pgpKeyChangePublicKeyValidationGroup.instance.validate().isValid) {
            this.isLoadPanelVisible = true;
            const publicKey = this.getPublicKeyFromData(this.selectedPgpKey);
            this.pgpKeysService.changePublicKey(this.selectedPgpKey.id, publicKey)
                .pipe(
                    mergeMap(_ => this.translateService.get('pgpKeys.msgPublicKeyUpdateComplete')),
                    finalize(() => this.isLoadPanelVisible = false))
                .subscribe(message => {
                    this.isChangePublicKeyPopupVisible = false;
                    this.loadDataSource();
                    notify(message, 'success', 3000);
                }, err => this.handleError(err));
        }
    }

    /**
     * Delete a PGP key
     * @param e Handle event
     * @param pgpKeyId Internal identifier of the PGP key to delete
     */
    public deletePgpKey(e, pgpKey: IOcbPgpKey) {
        e.event.stopPropagation();
        forkJoin(
            // Get translation messages
            this.translateService.get('pgpKeys.msgDeleteConfirm'),
            this.translateService.get('pgpKeys.msgDeleteConfirmTitle'))
            .pipe(
                // Display confirm popup
                mergeMap(messages => fromPromise(confirm(messages[0], messages[1]))),
                filter(accepted => accepted),
                // Display loader
                tap(_ => this.isLoadPanelVisible = true),
                // Send the RESET request
                mergeMap(_ => this.pgpKeysService.deletePgpKey(pgpKey.id)),
                // Display complete message
                mergeMap(_ => this.translateService.get('pgpKeys.msgPgpKeyDeleteComplete')),
                // Hide loader
                finalize(() => this.isLoadPanelVisible = false))
            .subscribe(
                message => {
                    this.loadDataSource();
                    notify(message, 'success', 3000);
                }, err => this.handleError(err));
    }

    /**
     * Display a PGP key
     * @param e Handle event
     */
    public displayPgpKey(e) {
        this.displayedPgpKey = e.data;
        this.isDisplayPopupVisible = true;
    }

    /**
     * Load data source
     */
    private loadDataSource(): void {
        this.showLoading$('_commons.loading')
            .pipe(
                mergeMap(_ => this.pgpKeysService.getPgpKeys(this.currentEnvironmentId)),
                finalize(() => this.pgpGrid.instance.endCustomLoading()))
            .subscribe(
                res => this.dataSource = res,
                err => this.handleError(err));
    }

    /**
     * Displays a loader over the data grid, with a localized message.
     * @param messageKey Localized message key.
     */
    private showLoading$(messageKey: string): Observable<void> {
        return this.translateService.get(messageKey).pipe(
            tap(message => this.pgpGrid.instance.beginCustomLoading(`${message}...`)),
        );
    }

    /**
     * Initialize new PGP key object
     */
    private getNewIOcbPgpKey(): IOcbPgpKey {
        return {
            id: null,
            environmentId: this.currentEnvironmentId,
            environmentName: null,
            name: '',
            publicKey: null,
            privateKey: null,
            privateKeyPassword: null,
            hasPublicKey: false,
            hasPrivateKey: false,
            hasPrivateKeyPassword: false,
        };
    }

    /**
     * Initialize new PGP key object from another one
     * @param data Data to duplicate
     */
    private getIOcbPgpKeyFromData(data: IOcbPgpKey): IOcbPgpKey {
        return {
            id: data.id,
            environmentId: data.environmentId,
            environmentName: data.environmentName,
            name: data.name,
            publicKey: data.publicKey,
            privateKey: data.privateKey,
            privateKeyPassword: data.privateKeyPassword,
            hasPublicKey: data.hasPublicKey,
            hasPrivateKey: data.hasPrivateKey,
            hasPrivateKeyPassword: data.hasPrivateKeyPassword,
        };
    }

    /**
     * Initialize new PGP key object (for create) from another one
     * @param data Data to duplicate
     */
    private getIOcbCPgpKeyFromData(data: IOcbPgpKey): IOcbCPgpKey {
        return {
            environmentId: data.environmentId ? data.environmentId : this.currentEnvironmentId,
            name: data.name,
            publicKey: data.publicKey,
            privateKey: data.privateKey,
            privateKeyPassword: data.privateKeyPassword,
        };
    }

    /**
     * Initialize new PGP key object (for update) from another one
     * @param data Data to duplicate
     */
    private getIOcbUPgpKeyFromData(data: IOcbPgpKey): IOcbUPgpKey {
        return {
            environmentId: data.environmentId ? data.environmentId : this.currentEnvironmentId,
            name: data.name,
        };
    }

    /**
     * Returns the required payload to update the private key and its password
     * @param data Data
     */
    private getPrivateKeyFromData(data: IOcbPgpKey): IOcbApiPrivateKeyParameter {
        return {
            key: data.privateKey,
            password: data.privateKeyPassword
        };
    }

    /**
     * Returns the required payload to update the public key
     * @param data Data
     */
    private getPublicKeyFromData(data: IOcbPgpKey): IOcbApiStringParameter {
        return {
            value: data.publicKey
        };
    }

    /**
     * Displays error message in notification
     */
    private handleError(error: any): void {
        let message: '';
        if (error instanceof HttpErrorResponse) {
            if (error.error.hasOwnProperty('message')) {
                message = error.error.message;
            } else if (error.error.hasOwnProperty('name')) {
                message = error.error.name;
            } else {
                message = error.error;
            }
        } else {
            message = error;
        }

        notify(message, 'error', 3000);
    }
}
